'use client'

import { useMutation } from '@tanstack/react-query'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import React from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import axios from '@/_lib/axiosInstance'
import { pushSimpleEvent } from '@/_utils/analytics/datalayer'

/**
 * Props for the contact form component.
 */
interface AContactFormProps {
	/** Placeholder texts for form inputs */
	placeholders: {
		/** Name field placeholder */
		name: string
		/** Surname field placeholder */
		surname: string
		/** Email field placeholder */
		email: string
		/** Phone field placeholder */
		phone: string
		/** Message field placeholder */
		message: string
	}
	/** Agreement texts (currently unused but kept for API compatibility) */
	agreements: string[]
	/** Submit button text */
	submitText: string
}

/**
 * Contact form data structure.
 */
interface ContactFormData {
	/** User's first name */
	firstName: string
	/** User's last name */
	lastName: string
	/** User's email address */
	email: string
	/** User's phone number (optional) */
	phone: string | undefined
	/** Message content */
	message: string
	/** Agreement to terms of service (currently unused) */
	agreedToTermsOfService: boolean
	/** Agreement to personal data protection policy */
	agreedToPersonalDataProtection: boolean
	/** Agreement to marketing purposes data processing */
	agreedToPersonalDataProcessingForMarketingPurposes: boolean
}

/** Minimum length for first name */
const MIN_NAME_LENGTH = 2
/** Minimum length for last name */
const MIN_SURNAME_LENGTH = 2
/** Minimum length for message */
const MIN_MESSAGE_LENGTH = 10
/** Minimum phone number digits */
const MIN_PHONE_DIGITS = 8
/** Maximum phone number digits */
const MAX_PHONE_DIGITS = 15

/**
 * Validation rules for contact form fields.
 */
const validationRules = {
	firstName: {
		required: 'validation.firstName.required',
		minLength: {
			value: MIN_NAME_LENGTH,
			message: 'validation.firstName.minLength'
		}
	},
	lastName: {
		required: 'validation.lastName.required',
		minLength: {
			value: MIN_SURNAME_LENGTH,
			message: 'validation.lastName.minLength'
		}
	},
	email: {
		required: 'validation.email.required',
		pattern: {
			value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
			message: 'validation.email.pattern'
		}
	},
	phone: {
		pattern: {
			value: /^[\+]?[1-9][\d\s\-\(\)\.]{7,20}$/,
			message: 'validation.phone.pattern'
		},
		validate: {
			/**
			 * Validates phone number length by counting only digits.
			 * @param value - Phone number string
			 * @returns True if valid, error message key otherwise
			 */
			onlyDigits: (value: string) => {
				if (!value) return true
				const digitsOnly = value.replace(/[\s\-\(\)\.]/g, '')
				return (
					(digitsOnly.length >= MIN_PHONE_DIGITS &&
						digitsOnly.length <= MAX_PHONE_DIGITS) ||
					'validation.phone.length'
				)
			}
		}
	},
	message: {
		required: 'validation.message.required',
		minLength: {
			value: MIN_MESSAGE_LENGTH,
			message: 'validation.message.minLength'
		}
	},
	agreedToTermsOfService: {
		required: 'validation.agreedToTermsOfService.required'
	},
	agreedToPersonalDataProtection: {
		required: 'validation.agreedToPersonalDataProtection.required'
	}
}

/**
 * Contact form component for footer section.
 * Allows users to send inquiries with:
 * - Personal information (name, surname)
 * - Contact details (email, optional phone)
 * - Message content
 * - Required GDPR agreements
 * - Optional marketing consent
 *
 * Features:
 * - Form validation with react-hook-form
 * - Async submission with TanStack Query
 * - Toast notifications for success/error states
 * - Automatic form reset on success
 *
 * @param placeholders - Input field placeholder texts
 * @param submitText - Submit button text
 */
const AContactForm: React.FC<AContactFormProps> = ({ placeholders, submitText }) => {
	const t = useTranslations('components.contact_form')
	const pathname = usePathname()
	const {
		register,
		handleSubmit,
		reset,
		formState: { errors }
	} = useForm<ContactFormData>({
		mode: 'onBlur'
	})

	/**
	 * Mutation hook for form submission.
	 * Removes empty phone field before sending to API.
	 */
	const mutation = useMutation({
		mutationFn: (data: ContactFormData) => {
			if (data.phone === '') {
				delete data.phone
			}

			return axios.post('/v1/web/contact-form', {
				...data,
				url: pathname
			})
		},
		onSuccess: (response, variables) => {
			pushSimpleEvent('generate_lead', {
				form_name: 'contact-form',
				form_destination: 'kontakt',
				form_location: pathname,
				value: 0,
				currency: 'EUR',
				user_data: {
					email: variables.email
				}
			})
			reset()
			toast.success(t('success'), {
				position: 'top-right',
				className: '!bg-medante-pink-secondary',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true
			})
		},
		onError: error => {
			toast.error(t('error'), {
				position: 'top-right',
				className: '!bg-medante-pink-secondary',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true
			})
			console.error('Error submitting form:', error)
		}
	})

	/**
	 * Form submission handler.
	 * @param data - Validated form data
	 */
	const onSubmit = (data: ContactFormData) => {
		mutation.mutate(data)
	}

	return (
		<>
			<form name='contact-form' onSubmit={handleSubmit(onSubmit)}>
				<div className='boomex-grid mgt-20'>
					<div className='col2 col1_mdm mgt-10'>
						{/* First Name Input */}
						<div className='mgt-20'>
							<div className='input_text_cont'>
								<input
									type='text'
									className={`input_text ${errors.firstName ? 'error' : ''}`}
									placeholder={placeholders.name}
									autoComplete='given-name'
									{...register('firstName', validationRules.firstName)}
								/>
								<label className='input_text_placeholder'>
									{placeholders.name}
								</label>
								{errors.firstName && (
									<div className='error-message'>
										{t(errors.firstName.message as string)}
									</div>
								)}
							</div>
						</div>

						{/* Last Name Input */}
						<div className='mgt-20'>
							<div className='input_text_cont'>
								<input
									type='text'
									className={`input_text ${errors.lastName ? 'error' : ''}`}
									placeholder={placeholders.surname}
									autoComplete='family-name'
									{...register('lastName', validationRules.lastName)}
								/>
								<label className='input_text_placeholder'>
									{placeholders.surname}
								</label>
								{errors.lastName && (
									<div className='error-message'>
										{t(errors.lastName.message as string)}
									</div>
								)}
							</div>
						</div>

						{/* Email Input */}
						<div className='mgt-20'>
							<div className='input_text_cont'>
								<input
									type='email'
									className={`input_text ${errors.email ? 'error' : ''}`}
									placeholder={placeholders.email}
									autoComplete='email'
									{...register('email', validationRules.email)}
								/>
								<label className='input_text_placeholder'>
									{placeholders.email}
								</label>
								{errors.email && (
									<div className='error-message'>
										{t(errors.email.message as string)}
									</div>
								)}
							</div>
						</div>

						{/* Phone Input */}
						<div className='mgt-20'>
							<div className='input_text_cont'>
								<input
									type='tel'
									className={`input_text ${errors.phone ? 'error' : ''}`}
									placeholder={placeholders.phone}
									autoComplete='tel'
									{...register('phone', {
										pattern: {
											value: /^[\+]?[1-9][\d\s\-\(\)\.]{7,20}$/,
											message: 'validation.phone.pattern'
										}
									})}
								/>
								<label className='input_text_placeholder'>
									{placeholders.phone}
								</label>
								{errors.phone && (
									<div className='error-message'>
										{t(errors.phone.message as string)}
									</div>
								)}
							</div>
						</div>
					</div>

					<div className='col2 col1_mdm mgt-10'>
						{/* Message Textarea */}
						<div className='mgt-20'>
							<div className='input_text_cont'>
								<textarea
									cols={15}
									rows={7}
									placeholder={placeholders.message}
									className={`input_text ${errors.message ? 'error' : ''}`}
									{...register('message', validationRules.message)}
								></textarea>
								<label className='input_text_placeholder'>
									{placeholders.message}
								</label>
								{errors.message && (
									<div className='error-message'>
										{t(errors.message.message as string)}
									</div>
								)}
							</div>
						</div>

						{/* GDPR Agreements */}
						<div className='pdt-5 pdt-5md'>
							{/* Required: GDPR Agreement */}
							<label className='input_checkbox_cont mgt-20'>
								<div className='flex '>
									<input
										type='checkbox'
										className={`input_checkbox ${
											errors.agreedToPersonalDataProtection ? 'error' : ''
										}`}
										{...register(
											'agreedToPersonalDataProtection',
											validationRules.agreedToPersonalDataProtection
										)}
									/>
									<div className='input_checkbox_text'>
										Oboznámil/a som sa a súhlasím s:{' '}
										<Link
											href='/legal/gdpr'
											title='Ochrana osobných údajov'
											prefetch={false}
										>
											Ochrana osobných údajov
										</Link>
										.
									</div>
								</div>
								{errors.agreedToPersonalDataProtection && (
									<div className='error-message'>
										{t(errors.agreedToPersonalDataProtection.message as string)}
									</div>
								)}
							</label>

							{/* Optional: Marketing Agreement */}
							<label className='input_checkbox_cont mgt-20'>
								<div className='checkbox-wrapper'>
									<input
										type='checkbox'
										className={`input_checkbox ${
											errors.agreedToPersonalDataProcessingForMarketingPurposes
												? 'error'
												: ''
										}`}
										{...register(
											'agreedToPersonalDataProcessingForMarketingPurposes'
										)}
									/>
									<div className='input_checkbox_text'>
										Oboznámil/a som sa a súhlasím so:{' '}
										<Link
											href='/legal/marketing-consent'
											title='Súhlas so spracúvaním osobných údajov na marketingové účely'
											prefetch={false}
										>
											Súhlas so spracúvaním osobných údajov na marketingové
											účely
										</Link>
										.
									</div>
								</div>
							</label>
						</div>
					</div>

					{/* Submit Button */}
					<div className='col1 mgt-50 text_center dflex flex-dir-column align-center'>
						<button
							type='submit'
							className='btn_basic type2'
							disabled={mutation.isPending}
						>
							<span>{submitText}</span>
							<span>
								<span>
									<svg className='sico'>
										<use href='/assets/layout/icons.svg#s_ar2_r' />
									</svg>
								</span>
								<span>
									<svg className='sico'>
										<use href='/assets/layout/icons.svg#s_ar2_r' />
									</svg>
								</span>
							</span>
						</button>
						<button className='btn_basic sent' disabled>
							<span>{submitText}</span>
							<div className='icons'>
								<img
									src={`${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/legacy/send.svg`}
									alt='Odoslať'
									width={30}
									height={30}
								/>
							</div>
						</button>
					</div>
				</div>
			</form>
		</>
	)
}

export default AContactForm
